import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps, CheckableType, ListItemIntent } from '../common/props';
import { CheckboxButton } from '../checkbox/CheckboxButton';
import { useCurrentTheme } from '../themes';
import { ListExtraSizeContext } from './List';
import { RadioButton } from '../radio/RadioButton';
import { LinkProps } from 'react-router-dom';
import { ColorDef, colorsMapBadge, ColorType } from '../common/colors';
import { InverseBgProvider } from '../theme/InverseBg';
import { Link } from '../link/Link';
import { ForwardedRef } from 'react';

export interface IListItemProps extends Omit<BaseProps, 'title'> {
  extra?: React.ReactNode;
  /**
   * Automaticky na blocku nastaví hover, lze vypnout pomocí parametru disableHover.
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;

  /**
   * Pokud se má  vyrenderovat jako link (a href), předá se tato properta a kždý řádek bude jako a href.
   */
  link?: LinkProps;

  /**
   * Vypne hover efekt nad řádky.
   */
  disableHover?: boolean;

  /**
   * Classname pro body
   */
  bodyClassName?: string;

  /**
   * Zda je položka zvýrazněna.
   */
  highlighted?: boolean;

  /**
   * Znemožní úpravu checkboxu
   */
  disabled?: boolean;

  /**
   * Zobrazí u itemu v levé části checkbox
   */
  checkable?: CheckableType;

  /**
   * Zda je checkbox vybrán - s checkable=undefined/false zbytečné
   */
  checked?: boolean;

  /**
   * Co dělat při výběru checkboxu - s checkable=undefined/false zbytečné
   * @param event mouse event
   */
  onCheck?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;

  /**
   * Classname na celý kontejner
   */
  className?: string;
  /**
   * Pokud je true, lze klikem na celý řádek zaškrtnout checkbox - je výlušné s onClick
   */
  rowCheck?: boolean;

  htmlTitle?: string;
  /**
   * Nadpis řádku, nejedná se o html title, ale opravdový nadpis řádku, není to vhodně pojmenované.
   */
  title?: React.ReactNode;
  fullHeight?: boolean;
  noScroll?: boolean;

  onDoubleClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /**
   * vyrenderuje header "zleva"
   */
  header?: React.ReactNode;

  /**
   * Intet pro Header
   */
  headerIntent?: ListItemIntent;
  /**
   * Barva pro Header
   */
  color?: ColorType;
  headerWidth?: number;
}

export type ListItemProps = IListItemProps;

/**
 * Komponenta obsahu listu.
 */
const ListItemIn: React.FC<
  ListItemProps & { innerRef?: ForwardedRef<HTMLDivElement | HTMLLinkElement> }
> = ({
  className,
  innerRef,
  style,
  children,
  extra,
  disabled,
  checkable,
  onCheck,
  checked,
  bodyClassName,
  rowCheck,
  highlighted,
  disableHover,
  onClick,
  onDoubleClick,
  link,
  title,
  htmlTitle,
  noScroll,
  fullHeight,
  header,
  headerIntent = 'default',
  color,
  headerWidth,
  ...other
}) => {
  let theme = useCurrentTheme();
  const listExtraSize = React.useContext(ListExtraSizeContext);
  const extraSize = listExtraSize.extraSize;
  let listItemHeaderWith = headerWidth || listExtraSize.headerWidth || 30;
  let colorVal: ColorDef | undefined;
  let colorStyle = {};
  if (color !== undefined) {
    colorVal = colorsMapBadge[color];
    colorStyle = {
      backgroundColor: colorVal.bg,
      color: colorVal.text,
      outlineColor: colorVal.border,
    };
  }
  let cls = classNames(Classes.LIST_ITEM, Classes.HOVER_CONTAINER, className, {
    [Classes.HOVER_ITEM_CONTAINER]: !disableHover,
    [Classes.LIST_ITEM_HIGHLIGHTED]: highlighted,
    [Classes.CHECKED_CONTAINER]: checked,
    [Classes.LIST_ITEM_CHECKABLE]: checkable,
    [Classes.CLICK_CONTAINER]:
      (rowCheck && !disabled) || onClick || onDoubleClick || link,
    [Classes.CONTAINER_FULL_HEIGHT]: fullHeight,
    [Classes.NO_SCROLL]: noScroll,
    [Classes.NO_USER_SELECT]: !!onDoubleClick,
    [Classes.LIST_ITEM_HAS_HEADER]: header != undefined,
  });

  if ((onClick || onDoubleClick) && link) {
    throw new Error(
      'Na ListItem byl současně předán onClick/onDoubleClick a link, to nní povoleno'
    );
  }

  if ((onClick || link || onDoubleClick) && rowCheck) {
    throw new Error(
      'Do ListItem předán onClick/onDoubleClick i rowCheck, jedná se o výlučné parametry'
    );
  }

  let realOnClick: any;
  if (onClick) {
    realOnClick = onClick;
  } else if (rowCheck) {
    realOnClick = onCheck;
  } else {
    realOnClick = undefined;
  }

  const extraWidth =
    extraSize !== undefined
      ? (theme.iconButtonWidth + theme.spacers[1]) * extraSize -
        theme.spacers[1] +
        theme.spacers[2]
      : undefined;
  const headerCls = classNames(Classes.LIST_ITEM_HEADER, {
    [Classes.LIST_ITEM_DEFAULT]: headerIntent === 'default',
    [Classes.LIST_ITEM_DEFAULT_INVERSE]: headerIntent === 'default-inverse',
    [Classes.LIST_ITEM_SECONDARY]: headerIntent === 'secondary',
    [Classes.LIST_ITEM_DANGER]: headerIntent === 'danger',
    [Classes.LIST_ITEM_SUCCESS]: headerIntent === 'success',
    [Classes.LIST_ITEM_WARNING]: headerIntent === 'warning',
    [Classes.LIST_ITEM_INACTIVE]: headerIntent === 'inactive',
  });
  const listItem = () => {
    return (
      <div className={'d-flex w-100'}>
        {header != undefined && (
          <InverseBgProvider
            inverse={headerIntent !== 'inactive' && headerIntent !== 'default'}
          >
            <div
              style={{ ...colorStyle, width: listItemHeaderWith }}
              className={headerCls}
            >
              {header}
            </div>
          </InverseBgProvider>
        )}
        <div className={'d-flex w-100'}>
          {checkable && (
            <div className={Classes.LIST_ITEM_CHECKER_CONT}>
              {checkable === true && (
                <CheckboxButton
                  noButtonStyle={rowCheck}
                  // @ts-ignore
                  onChange={onCheck}
                  disabled={disabled}
                  checked={checked}
                />
              )}
              {checkable === 'radio' && (
                <RadioButton
                  // @ts-ignore
                  onChange={(e) => {
                    !rowCheck &&
                      onCheck?.({
                        target: {
                          // @ts-ignore
                          value: true,
                        },
                      });
                  }}
                  disabled={disabled}
                  checked={checked}
                />
              )}
            </div>
          )}
          {(children || title) && (
            <div className={classNames(Classes.LIST_ITEM_BODY, bodyClassName)}>
              {title && <div className={Classes.LIST_ITEM_TITLE}>{title}</div>}
              {children}
            </div>
          )}
          {(extra || extraSize !== undefined) && (
            <div
              className={Classes.LIST_ITEM_EXTRA}
              style={{
                minWidth: extraWidth !== undefined ? extraWidth : 'initial',
              }}
            >
              {extra || <div></div>}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (link) {
    return (
      <ListExtraSizeContext.Provider value={{ extraSize: undefined }}>
        <Link
          // @ts-ignore
          ref={innerRef}
          {...other}
          {...link}
          className={cls}
          style={style}
          title={htmlTitle}
        >
          {listItem()}
        </Link>
      </ListExtraSizeContext.Provider>
    );
  } else {
    return (
      <ListExtraSizeContext.Provider value={{ extraSize: undefined }}>
        <div
          onClick={realOnClick}
          onDoubleClick={onDoubleClick}
          {...other}
          className={cls}
          style={style}
          title={htmlTitle}
          // @ts-ignore
          ref={innerRef}
        >
          {listItem()}
        </div>
      </ListExtraSizeContext.Provider>
    );
  }
};

export const ListItem = React.forwardRef<
  HTMLDivElement | HTMLLinkElement,
  ListItemProps
>((props, ref) => {
  return <ListItemIn innerRef={ref} {...props} />;
});
